import { useMatches, useRouteLoaderData } from 'react-router'
import { useCanonical, useOrigin } from '#app/seo/hooks.ts'
import { Seo, type SeoProps } from '#app/seo/seo.tsx'

export type SeoHandle = (args: {
	loaderData: unknown
	params: Record<string, string>
	pathname: string
	origin: string
	canonical: string
	generalSettings?: {
		websiteName?: string | null
		twitterSite?: string | null
		twitterCreator?: string | null
		defaultOgImage?: string | null
	}
}) => SeoProps | null | undefined

export function SeoHead() {
	const matches = useMatches()
	const origin = useOrigin()
	const canonical = useCanonical()
	const rootData = useRouteLoaderData('root') as
		| {
				generalSettings?: {
					websiteName?: string | null
					twitterSite?: string | null
					twitterCreator?: string | null
					defaultOgImage?: string | null
				}
		  }
		| undefined

	let descriptor: SeoProps | null = null
	for (const m of matches) {
		const handle = m.handle as { seo?: SeoHandle } | undefined
		if (typeof handle?.seo === 'function') {
			const next = handle.seo({
				loaderData: m.data,
				params: m.params as Record<string, string>,
				pathname: m.pathname,
				origin,
				canonical,
				generalSettings: rootData?.generalSettings,
			})
			if (next)
				descriptor = mergeWithDefaults(
					next,
					rootData?.generalSettings,
					canonical,
				)
		}
	}

	const fallback = mergeWithDefaults(
		{
			title: 'Business Data - SyncBolt.com',
			description:
				'Business Data. SyncBolt is a platform for creating and managing your business.',
			canonical,
			openGraph: {
				title: 'Business Data - SyncBolt.com',
				description:
					'Business Data. SyncBolt is a platform for creating and managing your business.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Business Data - SyncBolt.com',
				description:
					'Business Data. SyncBolt is a platform for creating and managing your business.',
			},
		},
		rootData?.generalSettings,
		canonical,
	)

	return <Seo {...(descriptor ?? fallback)} />
}

function mergeWithDefaults(
	input: SeoProps,
	generalSettings:
		| {
				websiteName?: string | null
				twitterSite?: string | null
				twitterCreator?: string | null
				defaultOgImage?: string | null
		  }
		| undefined,
	canonical: string,
): SeoProps {
	const siteName = generalSettings?.websiteName || 'SyncBolt'
	const og = input.openGraph || {}
	const tw = input.twitter || {}

	return {
		...input,
		canonical: input.canonical || canonical,
		openGraph: {
			siteName,
			image:
				og.image ||
				generalSettings?.defaultOgImage ||
				'https://syncbolt.com/img/social.jpg',
			url: og.url || input.canonical || canonical,
			...og,
		},
		twitter: {
			site: tw.site || generalSettings?.twitterSite || undefined,
			creator: tw.creator || generalSettings?.twitterCreator || undefined,
			image:
				tw.image ||
				generalSettings?.defaultOgImage ||
				'https://syncbolt.com/img/social.jpg',
			card: tw.card || 'summary_large_image',
			...tw,
		},
	}
}
